import React, {FC, useEffect} from "react";
import {Alert, Drawer, Spin, Timeline} from "antd";
import {
    CheckCircleOutlined,
    CloseCircleOutlined,
    HistoryOutlined,
    LinkOutlined,
    PlusCircleOutlined,
    RollbackOutlined,
    SwapOutlined,
    SyncOutlined,
    WarningOutlined,
} from "@ant-design/icons";
import {useGetPaymentActionLogsLazyQuery} from "../../generated/graphql";
import {PHASE_16_GO_LIVE_DATE} from "../../const";
import "./PaymentActionTimeline.less";

/**
 * Phase 16 / AUDIT-02 — Timeline-Drawer für die Payment-Action-Historie.
 *
 * Öffnet sich rechts (640px), sobald `scheduledPaymentId` gesetzt wird.
 * Die GraphQL-Query `getPaymentActionLogs` ist als useLazyQuery verdrahtet —
 * sie feuert erst, wenn der Drawer tatsächlich geöffnet wird. `destroyOnClose`
 * resettet den LazyQuery-State beim Schliessen, damit ein erneutes Öffnen
 * für eine andere ScheduledPayment neu lädt.
 *
 * Retroaktiver Banner (D-03): Am Ende der Timeline steht IMMER ein Hinweis
 * "Keine Historie vor {PHASE_16_GO_LIVE_DATE}" — auch wenn die Logs-Liste
 * leer ist. Das verhindert, dass Admin fälschlich davon ausgeht, dass nichts
 * passiert sei.
 *
 * AntD 4 API: Drawer nutzt `visible` (nicht `open` — das ist v5).
 */

type PaymentActionTimelineDrawerProps = {
    scheduledPaymentId: string | null;
    onClose: () => void;
};

// Phase 23 / Plan 23-06 / Task 1 — Dictionary erweitert um 4 neue Action-Types
// (Pitfall 7 — sonst fällt der Drawer-Switch beim Render-Default-Pfad zurück
// und der Admin sieht "attempt_cancelled" statt "Versuch storniert").
//
// Die 4 neuen Action-Types werden von den Phase-23 Admin-Mutations geschrieben:
//   - cancelMolliePaymentFromAdmin       → attempt_cancelled
//   - refreshMolliePaymentStatusFromAdmin → attempt_refreshed
//   - captureMolliePaymentFromAdmin       → attempt_captured
//   - createNewMolliePaymentAttemptFromAdmin → attempt_created
//
// JSON-Labels in `src/translations/{de,en}/views/PaymentActionTimeline.json`
// (Bucket `actionLabels`). Die Komponente konsumiert noch hartcoded DE-Strings
// (Bestandsentscheidung Phase 16) — die JSON-Keys sind für künftige
// i18n-Migration vorbereitet, ohne in Plan 23-06 das Phase-16-Pattern zu brechen.
export const ACTION_COLORS: {[key: string]: string} = {
    method_changed: "gold",
    link_sent_single: "blue",
    link_sent_bundle: "blue",
    refund_initiated: "orange",
    refund_completed: "orange",
    status_changed: "gray",
    chargeback_received: "red",
    chargeback_resolved: "green",
    // Phase 23 Plan 23-06 — 4 neue Action-Types
    attempt_cancelled: "default",
    attempt_refreshed: "blue",
    attempt_captured: "green",
    attempt_created: "blue",
    // Incident 18.08.2026 — Wartelistenplatz wurde online bezahlt. Rot wie
    // chargeback_received: kein Fehler der Zahlung, aber ein Vorgang, der eine
    // Admin-Entscheidung braucht (erstatten oder in Buchung umwandeln).
    paid_for_waitlist_booking: "red",
    // #1069 — Geld ist eingegangen, das die Rate nicht (mehr) braucht: zweite Zahlung auf
    // eine bereits bezahlte Rate bzw. Überzahlung über einen veralteten Zahlungsversuch
    // (z. B. alte Überweisungsreferenz nach Methodenwechsel). Braucht eine Admin-Entscheidung.
    bundle_conflict_skipped: "red",
    overpayment_stale_attempt: "red",
    // #1141 — Admin hat die Doppelzahlung als erledigt markiert.
    surplus_payment_resolved: "green",
};

export const ACTION_LABELS: {[key: string]: string} = {
    method_changed: "Zahlungsmethode geändert",
    link_sent_single: "Zahlungslink gesendet",
    link_sent_bundle: "Zahlungslink-Bündel gesendet",
    refund_initiated: "Rückerstattung angestoßen",
    refund_completed: "Rückerstattung abgeschlossen",
    status_changed: "Status geändert",
    chargeback_received: "Rückbuchung erhalten",
    chargeback_resolved: "Rückbuchung erledigt",
    // Phase 23 Plan 23-06 — 4 neue Action-Types
    attempt_cancelled: "Versuch storniert",
    attempt_refreshed: "Status aktualisiert",
    attempt_captured: "Erfasst",
    attempt_created: "Neuer Zahlungsversuch erstellt",
    // Incident 18.08.2026 — Label trägt die Handlungsanweisung mit, weil der
    // Vorgang selbst keine ist: der Admin muss entscheiden.
    paid_for_waitlist_booking: "Zahlung für Wartelistenplatz eingegangen — Erstattung prüfen",
    // #1069
    bundle_conflict_skipped: "Weitere Zahlung eingegangen, Rate war bereits bezahlt — Erstattung prüfen",
    // Der Server nutzt diese Action für Über- UND Unterzahlung sowie für Zahlungen auf
    // gelöschte Raten — das Label bleibt neutral, die Richtung steht in der Detailzeile.
    overpayment_stale_attempt: "Zahlung über früheren Zahlungsversuch weicht von der Rate ab — bitte prüfen",
    surplus_payment_resolved: "Doppelzahlung als erledigt markiert",
};

export const ACTION_ICONS: {[key: string]: React.ReactNode} = {
    method_changed: <SwapOutlined />,
    link_sent_single: <LinkOutlined />,
    link_sent_bundle: <LinkOutlined />,
    refund_initiated: <RollbackOutlined />,
    refund_completed: <RollbackOutlined />,
    status_changed: <CheckCircleOutlined />,
    chargeback_received: <WarningOutlined />,
    chargeback_resolved: <CloseCircleOutlined />,
    // Phase 23 Plan 23-06 — 4 neue Action-Types
    attempt_cancelled: <CloseCircleOutlined />,
    attempt_refreshed: <SyncOutlined />,
    attempt_captured: <CheckCircleOutlined />,
    attempt_created: <PlusCircleOutlined />,
    // Incident 18.08.2026
    paid_for_waitlist_booking: <WarningOutlined />,
    // #1069
    bundle_conflict_skipped: <WarningOutlined />,
    overpayment_stale_attempt: <WarningOutlined />,
    surplus_payment_resolved: <CheckCircleOutlined />,
};

export function renderMetadata(
    action: string,
    metadata: Record<string, unknown> | null | undefined,
): string {
    if (!metadata) return "";
    switch (action) {
        case "method_changed":
            return "Von " + String(metadata.from || "?") + " zu " + String(metadata.to || "?");
        case "link_sent_single":
            // PII-frei (T-16-01): metadata enthält bewusst keine E-Mail/Namen mehr.
            return "Zahlungslink an Bucher gesendet";
        case "link_sent_bundle":
            return "Zahlungslink-Bündel an Bucher gesendet";
        case "refund_initiated":
        case "refund_completed":
            return "Betrag: " + String(metadata.amount || "?") + " EUR";
        case "status_changed":
            return String(metadata.from || "?") + " → " + String(metadata.to || "?");
        case "chargeback_received":
            return (
                "Grund: " + String(metadata.reason || metadata.reasonCode || "unbekannt") +
                " (Gebühr: " + String(metadata.fee || "?") + " EUR)"
            );
        case "chargeback_resolved":
            return "Erledigt durch Admin";
        // Phase 23 Plan 23-06 — 4 neue Mollie-Admin-Action-Types
        case "attempt_cancelled":
            return metadata.reason ? "Grund: " + String(metadata.reason) : "";
        case "attempt_refreshed":
            return metadata.from && metadata.to
                ? String(metadata.from) + " → " + String(metadata.to)
                : "";
        case "attempt_captured":
            return metadata.amount
                ? "Betrag: " + String(metadata.amount) + " EUR"
                : "Vollständige Erfassung";
        case "attempt_created":
            return metadata.molliePaymentId
                ? "Mollie-ID: " + String(metadata.molliePaymentId)
                : "";
        // Incident 18.08.2026 — Server-Metadata: {bookingId, molliePaymentId?, amount?}
        // (siehe recordPaidWaitlistIncident in server/mollie/mollie.orchestration.ts).
        // Die bookingId ist im Drawer-Kontext redundant, der Betrag ist die
        // Entscheidungsgrundlage für die Erstattung.
        case "paid_for_waitlist_booking":
            return metadata.amount != null
                ? "Betrag: " + String(metadata.amount) + " EUR"
                : "";
        // #1069 — Server-Metadata: {molliePaymentId, paidAmount?, bundleSize, paymentProvider}
        // (mollie.webhook.ts, Zweig "conflict skipped"). paidAmount ist der Gesamtbetrag der
        // Mollie-Zahlung; ältere Einträge haben ihn nicht.
        case "bundle_conflict_skipped": {
            const parts: string[] = [];

            if (metadata.paidAmount != null) {
                // Bündel: die Mollie-Zahlung deckt mehrere Raten, zu viel ist nur der Anteil dieser Rate.
                parts.push(
                    Number(metadata.bundleSize) > 1
                        ? "Bündel-Zahlung gesamt: " + String(metadata.paidAmount) + " EUR (zu viel ist nur der Anteil dieser Rate)"
                        : "Betrag: " + String(metadata.paidAmount) + " EUR",
                );
            }
            if (metadata.molliePaymentId) {
                parts.push("Mollie-ID: " + String(metadata.molliePaymentId));
            }

            return parts.join(" · ");
        }
        // #1069 — Server-Metadata: {molliePaymentId, paidAmount, currentAmount?, difference?, reason?}.
        // difference > 0 = Überzahlung; `kind: "underpaid_*"` = Unterzahlung. Bei Unterzahlung
        // bezieht sich `difference` auf den vollen Ratenbetrag, nicht auf den offenen Rest einer
        // teilbezahlten Rate — deshalb dort nur den gezahlten Betrag zeigen. Bei Bündeln ist
        // `currentAmount` die Summe der betroffenen Raten, daher "Soll" statt "Rate".
        // `reason: "paid_on_tombstoned_rate"` = Zahlung auf eine gelöschte Rate.
        case "surplus_payment_resolved":
            return "";
        case "overpayment_stale_attempt": {
            const parts: string[] = [];

            if (String(metadata.kind || "").indexOf("underpaid") === 0 || Number(metadata.difference) < 0) {
                parts.push("Zu wenig gezahlt: " + String(metadata.paidAmount) + " EUR eingegangen — Rest bleibt offen");
            } else if (metadata.difference != null) {
                parts.push("Zu viel gezahlt: " + String(metadata.difference) + " EUR (gezahlt " + String(metadata.paidAmount) + ", Soll " + String(metadata.currentAmount) + ") — Erstattung prüfen");
            } else if (metadata.paidAmount != null) {
                parts.push((metadata.reason === "paid_on_tombstoned_rate" ? "Zahlung auf gelöschte Rate: " : "Betrag: ") + String(metadata.paidAmount) + " EUR");
            }
            if (metadata.molliePaymentId) {
                parts.push("Mollie-ID: " + String(metadata.molliePaymentId));
            }

            return parts.join(" · ");
        }
        default:
            try {
                return JSON.stringify(metadata);
            } catch (err) {
                return "";
            }
    }
}

function formatTimestamp(ts: unknown): string {
    if (!ts) return "";
    const d = new Date(typeof ts === "string" || typeof ts === "number" ? ts : String(ts));
    if (isNaN(d.getTime())) return String(ts);
    return d.toLocaleString("de-DE");
}

export const PaymentActionTimelineDrawer: FC<PaymentActionTimelineDrawerProps> = ({
    scheduledPaymentId,
    onClose,
}) => {
    const [fetchLogs, {data, loading, error}] = useGetPaymentActionLogsLazyQuery({
        fetchPolicy: "cache-and-network",
    });

    useEffect(() => {
        if (scheduledPaymentId) {
            fetchLogs({
                variables: {
                    scheduledPaymentId,
                    limit: 50,
                    offset: 0,
                },
            });
        }
    }, [scheduledPaymentId, fetchLogs]);

    const logs = (data && data.getPaymentActionLogs) || [];

    return (
        <Drawer
            className="payment-action-timeline-drawer"
            title={
                <span>
                    <HistoryOutlined style={{marginRight: 8}} />
                    {"Zahlungs-Historie"}
                </span>
            }
            placement="right"
            width={640}
            visible={!!scheduledPaymentId}
            onClose={onClose}
            destroyOnClose
        >
            {loading && !data ? <Spin tip="Lade..." /> : null}
            {error ? (
                <Alert
                    type="error"
                    message={error.message}
                    data-testid="PaymentActionTimelineError"
                />
            ) : null}
            {!error && (!loading || data) ? (
                <Timeline mode="left" data-testid="PaymentActionTimelineList">
                    {logs.map((log) => {
                        const action = log.action;
                        const color = ACTION_COLORS[action] || "gray";
                        const label = ACTION_LABELS[action] || action;
                        const icon = ACTION_ICONS[action];
                        const meta = renderMetadata(
                            action,
                            log.metadata as Record<string, unknown> | null | undefined,
                        );
                        return (
                            <Timeline.Item
                                key={log.id}
                                color={color}
                                dot={icon as React.ReactElement}
                                data-testid={"PaymentActionTimelineItem-" + action}
                            >
                                <div>
                                    <strong>{label}</strong>
                                </div>
                                {meta ? <div style={{color: "#595959"}}>{meta}</div> : null}
                                <small style={{color: "#8c8c8c"}}>
                                    {formatTimestamp(log.timestamp)}
                                </small>
                            </Timeline.Item>
                        );
                    })}
                    <Timeline.Item
                        color="gray"
                        data-testid="PaymentActionTimelineRetroBanner"
                    >
                        <em style={{color: "#8c8c8c"}}>
                            {"Keine Historie vor " + PHASE_16_GO_LIVE_DATE}
                        </em>
                    </Timeline.Item>
                </Timeline>
            ) : null}
        </Drawer>
    );
};

export default PaymentActionTimelineDrawer;
